import React, { useCallback, useEffect } from 'react';

import type { AmplitudeEnvironment } from '@repo/amplitude/client';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { SessionReplay } from 'contexts/analytics/SessionReplay';
import {
  SetUserTraits,
  TrackEvent,
  TrackEventOnce,
} from 'contexts/analytics/types';
import { useAmplitude } from 'contexts/analytics/useAmplitude';
import { useIdentify } from 'contexts/analytics/useIdentify';
import { usePage } from 'contexts/analytics/usePage';
import { usePlatformProperties } from 'contexts/analytics/usePlatformProperties';
import { useTrack } from 'contexts/analytics/useTrack';
import { useIsImpersonating } from 'providers/ImpersonationContext';
type AnalyticsProviderProps = {
  writeKey: string;
  amplitudeEnvironment: AmplitudeEnvironment;
  children: React.ReactNode;
};

export const AnalyticsProvider = ({
  children,
  writeKey,
  amplitudeEnvironment,
}: AnalyticsProviderProps) => {
  const analyticsBrowser = React.useMemo(
    () => (writeKey ? AnalyticsBrowser.load({ writeKey }) : null),
    [writeKey],
  );
  // Guides & Surveys stay out of impersonated sessions, so init waits until that's known.
  const isImpersonating = useIsImpersonating();
  const amplitude = useAmplitude(analyticsBrowser, amplitudeEnvironment, {
    enabled: isImpersonating !== null,
    skipEngagement: !!isImpersonating,
  });

  usePlatformProperties(analyticsBrowser);
  useIdentify(analyticsBrowser, amplitude);
  const pageEvent = usePage(analyticsBrowser);
  const { trackEvent, trackEventOnce } = useTrack(analyticsBrowser, amplitude);
  const setUserTraits: SetUserTraits = useCallback(
    traits => {
      analyticsBrowser?.identify(traits);
      amplitude?.setUserProperties(traits);
    },
    [analyticsBrowser, amplitude],
  );
  useEffect(() => {
    const links = document.querySelectorAll('a');
    const handleLinkClick = (e: MouseEvent) => {
      const a = (e.target as Element)?.closest('a') as HTMLAnchorElement;
      //check to see if the event was tracked already, this attribute can be added to components to skip double tracking
      const isTrackedAlready = a.getAttribute('data-tracking-source');
      if (!isTrackedAlready) {
        trackEvent('Clicked Link', {
          text: a.innerText,
          href: a.href,
          isExternal: a.target === '_blank',
        });
      }
    };
    links.forEach(a => {
      a.addEventListener('click', handleLinkClick);
    });
    return () => {
      links.forEach(a => {
        a.removeEventListener('click', handleLinkClick);
      });
    };
  }, [trackEvent]);

  return (
    <AnalyticsContext.Provider
      value={{ trackEvent, trackEventOnce, pageEvent, setUserTraits }}
    >
      <SessionReplay amplitude={amplitude} />
      {children}
    </AnalyticsContext.Provider>
  );
};
type AnalyticsContextType = {
  trackEvent: TrackEvent;
  trackEventOnce: TrackEventOnce;
  pageEvent: ReturnType<typeof usePage>;
  setUserTraits: SetUserTraits;
};
export const AnalyticsContext = React.createContext<AnalyticsContextType>({
  trackEvent: () => {
    // do nothing by default
  },
  trackEventOnce: () => {
    // do nothing by default
  },
  pageEvent: () => {
    // do nothing by default
  },
  setUserTraits: () => {
    // do nothing by default
  },
});

export const useAnalytics = () => React.useContext(AnalyticsContext);
